import React, { useState, useEffect, useRef } from "react";
import "./LoginPage.css";
import { Link, useNavigate } from "react-router-dom";
import { forgotPassword, resetPassword } from '../../controllers/accounts/Account';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faArrowLeft } from '@fortawesome/free-solid-svg-icons';

const ResetPassword = () => {
  const navigate = useNavigate();
  const inputRef = {
    email: useRef(null),
    password: useRef(null)
  };
  const ADMINDATA = JSON.parse(localStorage.getItem("ADMIN-INFO"));
  const TOKEN = localStorage.getItem("TOKEN");

  const [formData, setFormData] = useState([]);
  const [isOTPSent, setIsOTPSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState('');
  const [success, setSuccess] = useState('');

  const [countdown, setCountdown] = useState(60);
  const [isCounting, setIsCounting] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  /*************************************************************
   * Function Name  : handleChange
   * Purpose        : This function is used for store input value in states
   * Created Date   : 08-01-2024
   *************************************************************/
  const handleChange = (e) => {
    setFormData((preVal) => ({
      ...preVal,
      [e.target.name]: e.target.value
    }));
    setErrors((...preError) => ({
      ...preError,
      [e.target.name]: ''
    }));
  }; //End of Function

  /*************************************************************
   * Function Name  : handleforgotPassword
   * Purpose        : This function is used generate login OTP
   * Created Date   : 08-01-2024
   *************************************************************/
  const handleforgotPassword = async () => {
    try {
      if (!formData?.email) {
        setErrors((...preError) => ({
          ...preError,
          email: 'Email is required.'
        }));
      } else {
        setIsLoading(true);
        const options = { email: formData?.email };
        const result = await forgotPassword(options);
        if (result.status) {
          setIsOTPSent(true);
          setSuccess((preSuccess) => ({
            ...preSuccess,
            formSuccess: result.message
          }));
          setCountdown(60);
          setIsCounting(true);
        } else {
          setIsOTPSent(false);
          setErrors((...preError) => ({
            ...preError,
            formError: result.message
          }));
        }
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsLoading(false);
    }
  }; //End of Function

  /*************************************************************
   * Function Name  : handleResetPassword
   * Purpose        : This function is used handle verify otp
   * Created Date   : 29-01-2024
   *************************************************************/
  const handleResetPassword = async () => {
    try {
      if (!formData?.email) {
        setErrors((...preError) => ({ ...preError, email: 'Email is required.' }));
      } else if (!formData.otp) {
        setErrors((...preError) => ({ ...preError, otp: 'One Time Password is required.' }));
      } else if (!formData.new_password) {
        setErrors((...preError) => ({ ...preError, new_password: 'New Password is required.' }));
      } else if (!formData.confirm_password) {
        setErrors((...preError) => ({ ...preError, confirm_password: 'Confirm Password is required.' }));
      } else {
        if (formData.new_password === formData.confirm_password) {
          setIsLoading(true);
          const options = { email: formData?.email, otp: formData.otp, password: formData.new_password };
          const result = await resetPassword(options);
          if (result.status === true) {
            navigate(`${process.env.REACT_APP_LOGIN_URL || '/'}`);
          } else {
            setErrors((...preError) => ({
              ...preError,
              formError: "Something went wrong! Please try after some time."
            }));
          }
        } else {
          setErrors((...preError) => ({
            ...preError,
            formError: "New password and confirm password must match."
          }));
        }
      }
    } catch (error) {
      setErrors((...preError) => ({ ...preError, formError: "Something went wrong." }));
    } finally {
      setIsLoading(false);
    }
  }; //End of Function

  /*************************************************************
   * Function Name  : handleEmailChange
   * Purpose        : This function is used for Change email id
   * Created Date   : 08-01-2024
   *************************************************************/
  const handleEmailChange = async () => {
    setFormData((preVal) => ({ ...preVal, password: "", otp: "" }));
    setIsOTPSent(false);
    setCountdown(0);
  }; //End of Function

  /*************************************************************
   * Purpose        : This hooks is used for handle otp sent countdown
   * Created Date   : 08-01-2024
   *************************************************************/
  useEffect(() => {
    if (ADMINDATA || TOKEN) {
      navigate("/dashboard");
    }
    let countdownInterval;
    if (isCounting) {
      countdownInterval = setInterval(() => {
        if (countdown > 0) {
          setCountdown((prevCountdown) => prevCountdown - 1);
        } else {
          clearInterval(countdownInterval);
          setIsCounting(false);
        }
      }, 1000);
    } else {
      clearInterval(countdownInterval);
    }
    return () => { clearInterval(countdownInterval); };
  }, [countdown, isCounting]);

  const handleEmailKeyPress = (event) => {
    if (event.key === 'Enter') { handleforgotPassword(); }
  };

  const handleOtpKeyPress = (event) => {
    if (event.key === 'Enter') { handleResetPassword(); }
  };

  return (
    <div className="login-page-wrapper">
      <div className="login-card">
        <div className="login-logo-wrap">
          <img src="/logo.png" alt="Logo" />
        </div>

        {!isOTPSent && (
          <div className="step-block">
            <h2 className="login-title">Reset Password</h2>
            <p className="login-subtitle">Enter your email to receive a reset OTP</p>

            <div className="form-field">
              <label htmlFor="email">Email Address</label>
              <div className="field-input-wrap">
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="no-icon"
                  onKeyDown={handleEmailKeyPress}
                  onChange={handleChange}
                  value={formData?.email || ''}
                  placeholder="you@example.com"
                  autoComplete="email"
                  ref={inputRef.email}
                />
              </div>
              {errors?.email && <div className="field-error">{errors.email}</div>}
            </div>

            {errors?.formError && (
              <div className="form-error-banner">{errors.formError}</div>
            )}

            <button
              type="button"
              className="login-btn"
              onClick={handleforgotPassword}
              disabled={isLoading}
            >
              {isLoading && <span className="btn-spinner" />}
              {isLoading ? "Sending OTP…" : "Send OTP"}
            </button>

            <div className="form-footer-row" style={{ marginTop: '20px', justifyContent: 'center' }}>
              <Link to="/" className="link-red" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FontAwesomeIcon icon={faArrowLeft} /> Back to Login
              </Link>
            </div>
          </div>
        )}

        {isOTPSent && (
          <div className="step-block">
            <h2 className="login-title">Set New Password</h2>
            <p className="login-subtitle">Enter the OTP and choose a new password</p>

            {success?.formSuccess && (
              <div className="form-success-banner">
                {success.formSuccess}
                <Link to={false} onClick={handleEmailChange}>Change email</Link>
              </div>
            )}

            <div className="form-field">
              <label htmlFor="otp">One-Time Password</label>
              <div className="field-input-wrap">
                <input
                  type="text"
                  id="otp"
                  name="otp"
                  className="no-icon"
                  inputMode="numeric"
                  onChange={handleChange}
                  onKeyDown={handleOtpKeyPress}
                  placeholder="Enter OTP"
                  autoComplete="one-time-code"
                  maxLength={6}
                />
              </div>
              {errors?.otp && <div className="field-error">{errors.otp}</div>}
            </div>

            <div className="form-field">
              <label htmlFor="new_password">New Password</label>
              <div className="field-input-wrap">
                <input
                  type={showNewPassword ? "text" : "password"}
                  id="new_password"
                  name="new_password"
                  onChange={handleChange}
                  onKeyDown={handleOtpKeyPress}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                />
                <button type="button" className="eye-btn" onClick={() => setShowNewPassword(v => !v)} tabIndex={-1}>
                  <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              {errors?.new_password && <div className="field-error">{errors.new_password}</div>}
            </div>

            <div className="form-field">
              <label htmlFor="confirm_password">Confirm Password</label>
              <div className="field-input-wrap">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirm_password"
                  name="confirm_password"
                  onChange={handleChange}
                  onKeyDown={handleOtpKeyPress}
                  placeholder="Repeat new password"
                  autoComplete="new-password"
                />
                <button type="button" className="eye-btn" onClick={() => setShowConfirmPassword(v => !v)} tabIndex={-1}>
                  <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              {errors?.confirm_password && <div className="field-error">{errors.confirm_password}</div>}
            </div>

            <div className="resend-row">
              {isCounting ? (
                <span className="resend-countdown">Resend OTP in <strong>{countdown}s</strong></span>
              ) : (
                <button type="button" className="resend-link" onClick={handleforgotPassword}>
                  Resend OTP
                </button>
              )}
              <Link to={false} onClick={handleEmailChange} className="link-red">
                Change email
              </Link>
            </div>

            {errors?.formError && (
              <div className="form-error-banner">{errors.formError}</div>
            )}

            <button
              type="button"
              className="login-btn"
              onClick={handleResetPassword}
              disabled={isLoading}
            >
              {isLoading && <span className="btn-spinner" />}
              {isLoading ? "Saving…" : "Save New Password"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
